<template>
  <BittsModal
    title="Add more credits to your plan"
    variant="data"
    :hide-footer="true"
    :no-padding="true"
    :width="900"
    :action-loading="isSubmitting"
    class="credit-pack-purchase-modal"
    @closed="router.push('/billing')"
  >
    <div class="grid grid-cols-1 gap-24 md:grid-cols-2">
      <CreditPackOptionList
        :packs="packs"
        :selected-pricing-key="selectedPricingKey"
        :best-value-pricing-key="bestValuePricingKey"
        :is-disabled="isSubmitting"
        :is-loading="isLoadingPacks"
        :has-failed="catalogueError !== null"
        @select="selectPack"
        @retry="loadPacks"
      />
      <CreditPackOrderSummary
        v-model:is-authorized="isAuthorized"
        :pack="selectedPack"
        :organization-name="organizationName"
        :is-submitting="isSubmitting"
        :is-disabled="
          !selectedPack || currentCreditLimit === null || isUsageLoading
        "
        :failure="failure"
        @confirm="onConfirm"
        @cancel="router.push('/billing')"
      />
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsModal } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { useSharedCreditPacks } from '@/composables/credits/useCreditPacks';
import { useSharedCreditUsage } from '@/composables/credits/useCreditUsage';
import { useFlashesStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';

import CreditPackOptionList from './CreditPackOptionList.vue';
import CreditPackOrderSummary from './CreditPackOrderSummary.vue';

const router = useRouter();
const { addSuccessFlash } = useFlashesStore();
const isAuthorized = ref(false);

const {
  packs,
  isLoadingPacks,
  catalogueError,
  selectedPack,
  selectedPricingKey,
  bestValuePricingKey,
  purchaseState,
  loadPacks,
  selectPack,
  purchase,
} = useSharedCreditPacks();

const {
  data: usage,
  isLoading: isUsageLoading,
  retry: refreshUsage,
} = useSharedCreditUsage();
const { currentOrg } = storeToRefs(useRootStore());

const isSubmitting = computed(
  () => purchaseState.value.status === 'submitting',
);

const failure = computed(() =>
  purchaseState.value.status === 'failed' ? purchaseState.value.error : null,
);

const organizationName = computed(() => currentOrg.value.name);

const currentCreditLimit = computed(() => usage.value?.credit_limit ?? null);

onMounted(loadPacks);

watch(purchaseState, (state) => {
  if (state.status === 'failed') {
    isAuthorized.value = false;
    /* Refreshes the pack for a 404/422 before the customer can retry. */
    loadPacks();
    /* Only on a 409. An unconfirmed failure may already have committed, and
     * refreshing would hand the retry an expected_credit_limit that matches
     * the server, defeating the compare-and-swap that catches the duplicate. */
    if (state.error.type === 'http' && state.error.statusCode === 409)
      refreshUsage();
  }
  if (state.status !== 'succeeded') return;
  refreshUsage();
  addSuccessFlash({
    message: 'Credits added',
    description: 'Your credits are live now.',
  });
  router.push('/billing');
});

function onConfirm() {
  if (!isAuthorized.value) return;
  if (currentCreditLimit.value === null || isUsageLoading.value) return;
  purchase(currentCreditLimit.value);
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.credit-pack-purchase-modal :deep(.o-bitts-modal__content) {
  @apply p-24!;
}
</style>
